import React from 'react'
import { Card, CardContent, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Stack, TextField, Button, IconButton, Box, Select, MenuItem, FormControl } from '@mui/material'
import { Payment as PaymentIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import type { PaymentMethod } from './types'

interface Currency {
  id: number
  code: string
  name_a: string
  name_e: string
  price: number
  name_a_original: string
}

interface PaymentMethodOption {
  id: number | string
  name: string
}

interface PaymentMethodsProps {
  payments: PaymentMethod[]
  isMobile: boolean
  onUpdate: (index: number, field: keyof PaymentMethod, value: any) => void
  onRemove: (index: number) => void
  onAdd: () => void
  canRemove: (index: number) => boolean
  total: number
  currency: string
  currencies: Currency[]
  paymentMethods: (string | PaymentMethodOption)[]
  disabled?: boolean
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  payments,
  isMobile,
  onUpdate,
  onRemove,
  onAdd,
  canRemove,
  total,
  currency,
  currencies = [],
  paymentMethods = [],
  disabled
}) => {
  return (
    <Card elevation={1}>
      <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon fontSize="small" />
          طرق الدفع
        </Typography>

        {/* Desktop Table View */}
        {!isMobile && (
          <TableContainer sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1,
            overflowX: 'auto'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 100 }}>المبلغ</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 150 }}>طريقة الدفع</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', minWidth: 120 }}>العملة</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 80, textAlign: 'center' }}>إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment, index) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      <TextField
                        type="number"
                        value={payment.amount}
                        onChange={(e) => onUpdate(index, 'amount', Number(e.target.value))}
                        size="small"
                        sx={{ width: 100 }}
                        disabled={!!disabled}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ width: 150 }} disabled={!!disabled}>
                        <Select
                          value={payment.method || ''}
                          onChange={(e) => onUpdate(index, 'method', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>اختر طريقة الدفع</MenuItem>
                          {paymentMethods.filter(method => method).map((method, index) => {
                            if (typeof method === 'string') {
                              return (
                                <MenuItem key={index} value={method}>
                                  {method}
                                </MenuItem>
                              )
                            } else {
                              return (
                                <MenuItem key={method.id} value={method.id}>
                                  {method.name}
                                </MenuItem>
                              )
                            }
                          })}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ width: 120 }} disabled={!!disabled}>
                        <Select
                          value={payment.currency || ''}
                          onChange={(e) => onUpdate(index, 'currency', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>اختر العملة</MenuItem>
                          {currencies.filter(curr => curr && curr.id).map((curr) => (
                            <MenuItem key={curr.id} value={curr.id}>
                              {curr.name_a_original}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="error" 
                        size="small" 
                        onClick={() => onRemove(index)} 
                        disabled={!!disabled || !canRemove(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Mobile Card View */}
        {isMobile && (
          <Stack spacing={2}>
            {payments.map((payment, index) => (
              <Card key={payment.id} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="المبلغ"
                      type="number"
                      value={payment.amount}
                      onChange={(e) => onUpdate(index, 'amount', Number(e.target.value))}
                      size="small"
                      fullWidth
                      disabled={!!disabled}
                    />

                    <FormControl size="small" fullWidth disabled={!!disabled}>
                      <Select
                        value={payment.method || ''}
                        onChange={(e) => onUpdate(index, 'method', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>اختر طريقة الدفع</MenuItem>
                        {paymentMethods.filter(method => method).map((method, index) => {
                          if (typeof method === 'string') {
                            return (
                              <MenuItem key={index} value={method}>
                                {method}
                              </MenuItem>
                            )
                          } else {
                            return (
                              <MenuItem key={method.id} value={method.id}>
                                {method.name}
                              </MenuItem>
                            )
                          }
                        })}
                      </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth disabled={!!disabled}>
                      <Select
                        value={payment.currency || ''}
                        onChange={(e) => onUpdate(index, 'currency', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>اختر العملة</MenuItem>
                        {currencies.filter(curr => curr && curr.id).map((curr) => (
                          <MenuItem key={curr.id} value={curr.id}>
                            {curr.name_a_original}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        onClick={() => onRemove(index)}
                        disabled={!!disabled || !canRemove(index)}
                      >
                        حذف
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAdd}
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            disabled={!!disabled}
          >
            إضافة طريقة دفع
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              إجمالي المدفوعات: {total.toFixed(2)} {currency}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default PaymentMethods
